
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  PieChart, 
  Calculator,
  Download,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';
import { TextReportActions } from '../report/TextReportActions';
import { enhancedRiskAssessmentService } from '@/services/enhancedRiskAssessmentService';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { calculateAmortizationSchedule } from '../utils/amortizationCalculations';
import type { MortgageData } from '../CalculatorTabs';
import { BuydownScenario } from '../results/utils/buydownCalculations';
import { calculateBuydownAnalytics, calculateBuilderIncentiveImpact } from '@/utils/analytics/buydownAnalytics';
import { projectLongTermFinancials, calculateRateStrategyAnalytics } from '@/utils/analytics/longTermProjections';
import { identifyOptimizationOpportunities } from '@/utils/analytics/optimizationEngine';
import { calculateEnergyEfficiencyImpact } from '@/utils/analytics/energyEfficiencyCalculator';
import { BuydownAnalyticsCard } from './analytics/BuydownAnalyticsCard';
import { LongTermProjectionCard } from './analytics/LongTermProjectionCard';
import { OptimizationCard } from './analytics/OptimizationCard';
import { EnergyEfficiencyCard } from './analytics/EnergyEfficiencyCard';
import { MetricTooltip } from './analytics/MetricTooltip';
import { formatCurrency } from '../utils/formatters';

interface AnalyticsDashboardProps {
  mortgageData: MortgageData;
  onGenerateReport?: () => void;
  isGeneratingReport?: boolean;
  annualIncome?: number;
  buydownScenarios?: BuydownScenario[];
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  mortgageData,
  onGenerateReport,
  isGeneratingReport = false,
  annualIncome = 100000,
  buydownScenarios = []
}) => {
  const { trackRiskAssessment } = useAnalytics();
  // Calculate analytics
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const totalInterest = ((mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest) * mortgageData.loanTerm * 12) - loanAmount;
  const totalCost = mortgageData.homePrice + mortgageData.closingCosts.total + totalInterest;
  const loanToValue = (loanAmount / mortgageData.homePrice) * 100;
  const monthlyIncomeNeeded = mortgageData.monthlyPayment.total / 0.28; // 28% DTI rule
  const annualIncomeNeeded = monthlyIncomeNeeded * 12;

  // Calculate proper first year amortization
  const amortizationSchedule = React.useMemo(() => {
    if (loanAmount > 0 && mortgageData.interestRate > 0 && mortgageData.loanTerm > 0) {
      return calculateAmortizationSchedule(loanAmount, mortgageData.interestRate, mortgageData.loanTerm);
    }
    return null;
  }, [loanAmount, mortgageData.interestRate, mortgageData.loanTerm]);

  const firstYearPayments = React.useMemo(() => {
    if (amortizationSchedule?.monthlyPayments) {
      const firstTwelveMonths = amortizationSchedule.monthlyPayments.slice(0, 12);
      return {
        principal: firstTwelveMonths.reduce((sum, payment) => sum + payment.principalPayment, 0),
        interest: firstTwelveMonths.reduce((sum, payment) => sum + payment.interestPayment, 0)
      };
    }
    // Fallback to original calculation if amortization fails
    return {
      principal: mortgageData.monthlyPayment.principal * 12,
      interest: mortgageData.monthlyPayment.interest * 12
    };
  }, [amortizationSchedule, mortgageData.monthlyPayment]);

  // Enhanced risk assessment with seller credits
  const riskAssessment = enhancedRiskAssessmentService.calculateRiskAssessment(mortgageData, annualIncome);
  const cashFlowAnalysis = enhancedRiskAssessmentService.calculateCashFlowAnalysis(mortgageData);

  // Track risk assessment when calculation is complete
  React.useEffect(() => {
    if (isCalculationComplete) {
      trackRiskAssessment(riskAssessment);
    }
  }, [mortgageData.homePrice, mortgageData.downPayment, mortgageData.sellerCredit, mortgageData.closingCosts.total]);
  
  // Performance metrics
  const interestToLoanRatio = (totalInterest / loanAmount) * 100;
  const closingCostRatio = (mortgageData.closingCosts.total / mortgageData.homePrice) * 100;
  const downPaymentRatio = mortgageData.downPaymentPercentage;

  // Enhanced risk assessment display
  const getRiskColor = (level: string) => {
    switch (level) {
      case 'Low': return { color: 'text-green-600', bgColor: 'bg-green-100' };
      case 'Medium': return { color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
      case 'High': return { color: 'text-orange-600', bgColor: 'bg-orange-100' };
      case 'Critical': return { color: 'text-red-600', bgColor: 'bg-red-100' };
      default: return { color: 'text-gray-600', bgColor: 'bg-gray-100' };
    }
  };

  const riskDisplay = getRiskColor(riskAssessment.riskLevel);
  const isCalculationComplete = mortgageData.monthlyPayment.total > 0 && mortgageData.closingCosts.total > 0;

  // New analytics calculations
  const buydownAnalytics = useMemo(() => {
    if (buydownScenarios && buydownScenarios.length > 0) {
      return calculateBuydownAnalytics(buydownScenarios);
    }
    return null;
  }, [buydownScenarios]);

  const builderIncentiveAnalytics = useMemo(() => {
    if (buydownScenarios && buydownScenarios.length > 0) {
      return calculateBuilderIncentiveImpact(buydownScenarios, mortgageData.closingCosts.total);
    }
    return null;
  }, [buydownScenarios, mortgageData.closingCosts.total]);

  const longTermProjection = useMemo(() => {
    return projectLongTermFinancials(mortgageData);
  }, [mortgageData]);

  const rateStrategyAnalytics = useMemo(() => {
    return calculateRateStrategyAnalytics(mortgageData);
  }, [mortgageData]);

  const optimizationOpportunities = useMemo(() => {
    return identifyOptimizationOpportunities(mortgageData, buydownScenarios || []);
  }, [mortgageData, buydownScenarios]);

  const [hersIndex, setHersIndex] = React.useState(75);
  const [baselineHers, setBaselineHers] = React.useState(100);
  const [annualUtilityCost, setAnnualUtilityCost] = React.useState(2500);

  const energyEfficiencyImpact = useMemo(() => {
    return calculateEnergyEfficiencyImpact(
      mortgageData,
      hersIndex,
      baselineHers,
      annualUtilityCost
    );
  }, [mortgageData, hersIndex, baselineHers, annualUtilityCost]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Analytics Dashboard</h3>
          <p className="text-muted-foreground">Advanced metrics and insights for your mortgage</p>
        </div>
        
        {isCalculationComplete && (
          <TextReportActions 
            mortgageData={mortgageData}
            disabled={!isCalculationComplete}
          />
        )}
      </div>

      {/* Enhanced Risk Assessment with Seller Credits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Enhanced Risk Assessment
            <MetricTooltip metric="riskAssessment" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <Badge className={`${riskDisplay.bgColor} ${riskDisplay.color} border-0`}>
              {riskAssessment.riskLevel} Risk
            </Badge>
            <span className="text-sm text-muted-foreground">
              Risk Score: {riskAssessment.riskScore}/100 <MetricTooltip metric="riskScore" />
            </span>
            <Progress value={riskAssessment.riskScore} className="h-2 flex-1 max-w-32" />
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium">Cash Flow Impact</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm">Original Cash to Close:</span>
                  <span className="font-medium">{formatCurrency(riskAssessment.cashToClose)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Seller Credit Applied:</span>
                  <span className="font-medium text-blue-600">-{formatCurrency(riskAssessment.sellerCreditAmount)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-medium">Final Cash to Close:</span>
                  <span className="text-lg font-bold text-green-600">{formatCurrency(riskAssessment.adjustedCashToClose)}</span>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Seller Credit Effectiveness <MetricTooltip metric="sellerCreditEffectiveness" /></p>
                  <p className="text-lg font-semibold text-blue-600">{cashFlowAnalysis.effectivenessRating}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium">Risk Factors</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm">Loan-to-Value <MetricTooltip metric="loanToValue" iconSize={12} /></span>
                    <span className="text-sm">{riskAssessment.factors.loanToValue.toFixed(1)}%</span>
                  </div>
                  <Progress value={riskAssessment.factors.loanToValue} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm">Cash-to-Close Ratio <MetricTooltip metric="cashToCloseRatio" iconSize={12} /></span>
                    <span className="text-sm">{riskAssessment.factors.cashToCloseRatio.toFixed(1)}%</span>
                  </div>
                  <Progress value={Math.min(riskAssessment.factors.cashToCloseRatio, 100)} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm">Seller Credit Dependency <MetricTooltip metric="sellerCreditDependency" iconSize={12} /></span>
                    <span className="text-sm">{riskAssessment.factors.sellerCreditDependency.toFixed(1)}%</span>
                  </div>
                  <Progress value={riskAssessment.factors.sellerCreditDependency} className="h-2" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Metrics */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Cost Analysis
              <MetricTooltip metric="costAnalysis" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Home Price <MetricTooltip metric="homePrice" iconSize={12} /></span>
                  <span className="text-sm">{formatCurrency(mortgageData.homePrice)}</span>
                </div>
                <Progress value={50} className="h-2" />
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Total Interest <MetricTooltip metric="totalInterest" iconSize={12} /></span>
                  <span className="text-sm">{formatCurrency(totalInterest)}</span>
                </div>
                <Progress value={interestToLoanRatio} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">
                  {interestToLoanRatio.toFixed(1)}% of loan amount
                </p>
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Closing Costs <MetricTooltip metric="closingCosts" iconSize={12} /></span>
                  <span className="text-sm">{formatCurrency(mortgageData.closingCosts.total)}</span>
                </div>
                <Progress value={closingCostRatio} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">
                  {closingCostRatio.toFixed(1)}% of home price
                </p>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Cost of Ownership <MetricTooltip metric="totalCostOfOwnership" iconSize={12} /></span>
                <span className="text-xl font-bold text-red-600">
                  {formatCurrency(totalCost)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Income Requirements
              <MetricTooltip metric="incomeRequirements" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">
                {formatCurrency(annualIncomeNeeded)}
              </p>
               <p className="text-sm text-muted-foreground">
                 Recommended annual income <MetricTooltip metric="recommendedIncome" iconSize={12} />
               </p>
               <p className="text-xs text-muted-foreground mt-1">
                 Based on 28% debt-to-income ratio <MetricTooltip metric="dtiRule" iconSize={12} />
               </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="text-center">
                <p className="text-lg font-semibold">
                  {formatCurrency(monthlyIncomeNeeded)}
                </p>
                <p className="text-xs text-muted-foreground">Monthly Income</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold">
                  {formatCurrency(mortgageData.monthlyPayment.total)}
                </p>
                <p className="text-xs text-muted-foreground">Monthly Payment</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Info className="w-4 h-4" />
                <span>This assumes no other monthly debt obligations</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Composition */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
             <PieChart className="w-5 h-5" />
            Payment Composition Over Time
            <MetricTooltip metric="paymentComposition" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Year 1 Principal <MetricTooltip metric="year1Principal" iconSize={12} /></p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(Math.round(firstYearPayments.principal))}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Year 1 Interest <MetricTooltip metric="year1Interest" iconSize={12} /></p>
              <p className="text-lg font-semibold text-red-600">
                {formatCurrency(Math.round(firstYearPayments.interest))}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Principal</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(loanAmount)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Interest</p>
              <p className="text-lg font-semibold text-red-600">
                {formatCurrency(totalInterest)}
              </p>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between text-sm">
              <span>Interest vs Principal Ratio <MetricTooltip metric="interestPrincipalRatio" iconSize={12} /></span>
              <span className="font-medium">
                {(totalInterest / loanAmount).toFixed(2)}:1
              </span>
            </div>
            <Progress 
              value={(totalInterest / (totalInterest + loanAmount)) * 100} 
              className="h-2 mt-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
             <TrendingUp className="w-5 h-5" />
            Key Insights
            <MetricTooltip metric="keyInsights" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {loanToValue <= 80 ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                )}
                <div>
                  <p className="font-medium">PMI Status <MetricTooltip metric="pmiStatus" iconSize={12} /></p>
                  <p className="text-sm text-muted-foreground">
                    {loanToValue <= 80 ? 'No PMI required' : 'PMI required until 80% LTV'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {downPaymentRatio >= 20 ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-orange-600" />
                )}
                <div>
                  <p className="font-medium">Down Payment <MetricTooltip metric="downPaymentInsight" iconSize={12} /></p>
                  <p className="text-sm text-muted-foreground">
                    {downPaymentRatio >= 20 ? 'Excellent down payment' : 'Consider increasing if possible'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {interestToLoanRatio <= 50 ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                )}
                <div>
                  <p className="font-medium">Interest Cost <MetricTooltip metric="interestCostInsight" iconSize={12} /></p>
                  <p className="text-sm text-muted-foreground">
                    {interestToLoanRatio <= 50 ? 'Reasonable interest cost' : 'Consider shorter term or better rate'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {closingCostRatio <= 3 ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                )}
                <div>
                  <p className="font-medium">Closing Costs <MetricTooltip metric="closingCostsInsight" iconSize={12} /></p>
                  <p className="text-sm text-muted-foreground">
                    {closingCostRatio <= 3 ? 'Normal closing costs' : 'Higher than average closing costs'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="w-full mt-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="buydown">Buydown Analysis</TabsTrigger>
          <TabsTrigger value="long-term">Long-Term Strategy</TabsTrigger>
          <TabsTrigger value="optimization">Optimization</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Current dashboard content stays here - everything above this */}
        </TabsContent>

        <TabsContent value="buydown" className="space-y-6 mt-6">
          {buydownAnalytics ? (
            <>
              <BuydownAnalyticsCard analytics={buydownAnalytics} />
              {builderIncentiveAnalytics && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">Builder Incentive Impact <MetricTooltip metric="builderCredit" /></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Builder Credit <MetricTooltip metric="builderCredit" iconSize={12} /></p>
                        <p className="text-3xl font-bold text-blue-600">
                          {formatCurrency(builderIncentiveAnalytics.totalBuilderCredit)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {builderIncentiveAnalytics.builderCreditPercentage.toFixed(2)}% of purchase price
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Cash-to-Close Reduction <MetricTooltip metric="cashToCloseReduction" iconSize={12} /></p>
                        <p className="text-3xl font-bold text-green-600">
                          {builderIncentiveAnalytics.cashToCloseReduction.toFixed(1)}%
                        </p>
                        <Badge className="mt-2">{builderIncentiveAnalytics.effectivenessRating}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>Configure buydown scenarios in the Results tab to see detailed analysis here.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="long-term" className="space-y-6 mt-6">
          <LongTermProjectionCard projection={longTermProjection} />
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">Rate Strategy Comparison <MetricTooltip metric="rateStrategy" /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                {rateStrategyAnalytics.termComparison.map(term => (
                  <div key={term.term} className={`p-4 rounded-lg border-2 ${term.term === rateStrategyAnalytics.recommendedTerm ? 'bg-primary/5 border-primary' : 'bg-muted border-border'}`}>
                    <p className="text-lg font-bold">{term.term}-Year</p>
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Monthly:</span>
                        <span className="font-semibold">{formatCurrency(term.monthlyPayment)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Interest:</span>
                        <span className="font-semibold text-red-600">{formatCurrency(term.totalInterest)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Years to 20% Equity:</span>
                        <span className="font-semibold">{term.yearsToEquity.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-900">
                <p className="text-sm text-blue-800 dark:text-blue-200">{rateStrategyAnalytics.rationale}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="optimization" className="space-y-6 mt-6">
          <EnergyEfficiencyCard 
            impact={energyEfficiencyImpact}
            onHersIndexChange={setHersIndex}
            onBaselineChange={setBaselineHers}
            onUtilityCostChange={setAnnualUtilityCost}
          />
          
          <OptimizationCard opportunities={optimizationOpportunities} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
